import { scorecardDraftSchema, type ScorecardDraft } from '@nest-recruit/shared';
import { z } from 'zod';

/**
 * F11.05 / F11.06 — the scorecard's copy on this device. "Offline draft persistence is a
 * hard requirement, not polish" (master plan S20): every keystroke is written here first,
 * so a dropped connection, a closed tab or a crashed browser loses nothing, and the
 * server is told when it can be reached. Pure, so the rules are tested without a browser.
 */

const storedSchema = z.object({
  draft: scorecardDraftSchema,
  /** The server revision this device last saw — a save from an older one is a conflict. */
  baseRevision: z.number().int().min(0),
  /** Changes the server has not confirmed yet. */
  isDirty: z.boolean(),
  /** A change was made while the server could not be reached — its save is a sync (F11.06). */
  wasOffline: z.boolean(),
  updatedAt: z.string(),
});
export type StoredDraft = z.infer<typeof storedSchema>;

export interface ServerCopy {
  readonly draft: ScorecardDraft;
  readonly revision: number;
  readonly savedAt: string | null;
}

export type Reconciled =
  | { readonly kind: 'server'; readonly draft: ScorecardDraft; readonly baseRevision: number }
  | { readonly kind: 'local'; readonly draft: ScorecardDraft; readonly baseRevision: number; readonly wasOffline: boolean }
  | { readonly kind: 'conflict'; readonly draft: ScorecardDraft; readonly server: ServerCopy; readonly wasOffline: boolean };

export function storageKey(userId: string, scorecardId: string): string {
  return `nr.scorecard.${userId}.${scorecardId}`;
}

/** A missing, unreadable or foreign value is treated as nothing stored — never a crash. */
export function readStored(storage: Pick<Storage, 'getItem'>, key: string): StoredDraft | null {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = storedSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** False when the device refused (storage full, private mode) — the screen then says so. */
export function writeStored(storage: Pick<Storage, 'setItem'>, key: string, value: StoredDraft): boolean {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Which copy the cockpit shows when the server's arrives: the server's when this device
 * has nothing unsent; this device's when it was edited from the server's current
 * revision; and a question when both moved — this device's being the default answer
 * ("local wins, prompt shown", F11.06).
 */
export function reconcile(stored: StoredDraft | null, server: ServerCopy): Reconciled {
  if (!stored || !stored.isDirty) return { kind: 'server', draft: server.draft, baseRevision: server.revision };
  if (stored.baseRevision === server.revision) return { kind: 'local', draft: stored.draft, baseRevision: stored.baseRevision, wasOffline: stored.wasOffline };
  if (sameDraft(stored.draft, server.draft)) return { kind: 'server', draft: server.draft, baseRevision: server.revision };
  return { kind: 'conflict', draft: stored.draft, server, wasOffline: stored.wasOffline };
}

export function sameDraft(a: ScorecardDraft, b: ScorecardDraft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
